/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable no-console */
import { isAxiosError } from 'axios'

export interface Logger {
  debug: LogFunction
  info: LogFunction
  warn: LogFunction
  error: LogFunction
  timerStart: TimerFunction
  timerElapsed: TimerFunction
  timerEnd: TimerFunction
}

export interface SendLogMessagePayload<T> {
  getDefaultContext: DefaultContextFn
  logger: T
  level: LOG_LEVEL_TYPE
  message: string
  context?: any
  error?: unknown
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LogFunction = (message: string, context?: any, error?: unknown) => void

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TimerFunction = (timerName: string, context?: any, error?: unknown) => void

const SUPPPORTED_LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const
export type LOG_LEVEL_TYPE = (typeof SUPPPORTED_LOG_LEVELS)[number]

export type DefaultContextFn = () => Record<string, any>

export function isAnError(maybeError: any): maybeError is Error {
  return (
    isAxiosError(maybeError) ||
    maybeError instanceof Error ||
    (maybeError && !!maybeError.stack && !!maybeError.message)
  )
}

export function getErrorFromContext(context: any): Error | null {
  if (!context) return null

  if (isAnError(context)) return context
  if (isAnError(context.error)) return context.error
  if (isAnError(context.err)) return context.err
  if (isAnError(context.e)) return context.e

  return null
}

const logLevelMap = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
} as const

export function getConfiguredLogLevel(): LOG_LEVEL_TYPE {
  const logLevel = (process.env.NEXT_PUBLIC_LOG_LEVEL || 'info').toLowerCase() as LOG_LEVEL_TYPE
  return logLevelMap[logLevel] ?? 'info'
}

const logLevelNumberMap = { debug: 0, info: 1, warn: 2, error: 3 }

export function getLogLevelNumber(logLevel: LOG_LEVEL_TYPE): number {
  return logLevelNumberMap[logLevel] ?? 1
}

export function getNoopLogger(): Logger {
  return {
    debug() {},
    info() {},
    warn() {},
    error() {},
    timerStart() {},
    timerElapsed() {},
    timerEnd() {},
  }
}

interface CreateLoggerParams<T> {
  logger: T
  getDefaultContext: DefaultContextFn
  sendLogMessage: (payload: SendLogMessagePayload<T>) => void
}

export function createLogger<T>({ logger, getDefaultContext, sendLogMessage }: CreateLoggerParams<T>): Logger {
  const timers: Record<string, number> = {}

  const sanitizedSendLogMessage = (payload: SendLogMessagePayload<T>) => {
    const sanitizedContext = sanitize(payload.context)
    const sanitizedError = sanitize(payload.error)
    sendLogMessage({ ...payload, context: sanitizedContext, error: sanitizedError })
  }

  return {
    debug(message, context, error) {
      sanitizedSendLogMessage({ level: 'debug', message, context, error, getDefaultContext, logger })
    },
    info(message, context, error) {
      sanitizedSendLogMessage({ level: 'info', message, context, error, getDefaultContext, logger })
    },
    warn(message, context, error) {
      sanitizedSendLogMessage({ level: 'warn', message, context, error, getDefaultContext, logger })
    },
    error(message, context, error) {
      sanitizedSendLogMessage({ level: 'error', message, context, error, getDefaultContext, logger })
    },
    timerStart(timerName, context, error) {
      const d = new Date()
      timers[timerName] = d.getTime()
      const message = `Timer "${timerName}" started at: ${d.toISOString()}`
      this.info(message, context, error)
    },
    timerElapsed(timerName, context, error) {
      const startTime = timers[timerName]

      if (!startTime) {
        const message = `Timer "${timerName}" not found. This timer either was never started or has already been ended.`
        return this.info(message, context, error)
      }

      const d = new Date()
      const elapsedMs = d.getTime() - startTime
      const message = `Timer "${timerName}" elapsed: ${elapsedMs}ms`

      this.info(message, { elapsedMs, ...context }, error)
    },
    timerEnd(timerName, context, error) {
      const startTime = timers[timerName]

      if (!startTime) {
        const message = `Timer "${timerName}" not found. This timer either was never started or has already been ended.`
        return this.info(message, context, error)
      }

      const d = new Date()
      const elapsedMs = d.getTime() - startTime
      const message = `Timer "${timerName}" ended at: ${d.toISOString()} with a total time of: ${elapsedMs}ms`
      delete timers[timerName]

      this.info(message, { elapsedMs, ...context }, error)
    },
  }
}

const MAX_SANITIZE_DEPTH = 30
const SENSITIVE_KEYS = ['authorization', 'client_secret']

function sanitize(obj: unknown, seen = new WeakSet<object>(), depth = 0): any {
  if (obj === null || obj === undefined) return obj
  if (typeof obj === 'string') return sanitizeSensitiveString(obj)
  if (typeof obj !== 'object') return obj

  // Prevent infinite recursion from circular references
  if (seen.has(obj)) return '[Circular Reference]'

  // Prevent stack overflow from deeply nested objects
  if (depth > MAX_SANITIZE_DEPTH) return '[Max Depth Exceeded]'

  seen.add(obj)

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitize(item, seen, depth + 1))
  }

  // For Error objects, sanitize custom properties in place and return the original
  // For regular objects, create a sanitized copy
  const isError = obj instanceof Error
  const copy: any = isError ? obj : {}
  const entries = Object.entries(obj)

  for (const [key, value] of entries) {
    const keyLower = key.toLowerCase()
    if (SENSITIVE_KEYS.includes(keyLower)) {
      copy[key] = '[REDACTED]'
    } else {
      copy[key] = sanitize(value, seen, depth + 1)
    }
  }

  return copy
}

/**
 * Sanitizes a string that may contain sensitive data in URL-encoded or JSON format.
 * - URL-encoded: client_secret=value -> client_secret=[REDACTED]
 * - JSON: "client_secret":"value" -> "client_secret":"[REDACTED]"
 */
function sanitizeSensitiveString(str: string): string {
  const strLower = str.toLowerCase()
  let result = str

  for (const key of SENSITIVE_KEYS) {
    // The vast majority of strings won't contain sensitive keys, so exit as soon as possible.
    if (!strLower.includes(key)) continue

    // Only run expensive URL-encoded regex if the pattern could match
    if (strLower.includes(`${key}=`)) {
      const urlEncodedRegex = new RegExp(`(${key})=([^&]*)`, 'gi')
      result = result.replace(urlEncodedRegex, '$1=[REDACTED]')
    }

    // Only run expensive JSON regex if the pattern could match
    if (strLower.includes(`"${key}"`)) {
      const jsonRegex = new RegExp(`("${key}"\\s*:\\s*)"([^"]*)"`, 'gi')
      result = result.replace(jsonRegex, '$1"[REDACTED]"')
    }
  }

  return result
}
